// app/features/customers/components/CustomerInfoSidebarComponent.tsx
'use client';

import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { CustomerStory } from '../customers.interface';
import { accentLightBgClasses } from '../utils/accentColors';

interface NavItem {
  id: string;
  label: string;
}

interface CustomerInfoSidebarProps {
  story: CustomerStory;
  navItems: NavItem[];
  className?: string;
}

export function CustomerInfoSidebarComponent({ story, navItems, className }: CustomerInfoSidebarProps) {
  const [activeSection, setActiveSection] = useState<string>(navItems[0]?.id || '');

  useEffect(() => {
    // Create intersection observer to track which section is in view
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Trigger when section is in upper portion of viewport
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [navItems]);

  return (
    <aside className={cn('hidden lg:block w-80 flex-shrink-0', className)}>
      <div className="sticky top-24 space-y-6">
        {/* Company Image - Like Clay */}
        <div className={cn(
          'aspect-square rounded-xl overflow-hidden flex items-center justify-center',
          accentLightBgClasses[story.accentColor]
        )}>
          {story.logoUrl ? (
            <img 
              src={story.logoUrl} 
              alt={story.customerName} 
              className="w-full h-full object-cover" 
            />
          ) : (
            <span className="text-6xl font-bold text-black/20">
              {story.customerName.charAt(0)}
            </span>
          )}
        </div>
        
        {/* Navigation - Like Clay - Updates on scroll */}
        <nav className="space-y-1">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={cn(
                  'flex items-center gap-3 py-2.5 text-base transition-colors',
                  isActive 
                    ? 'text-black font-semibold' 
                    : 'text-text-secondary hover:text-black'
                )}
              >
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-black" />}
                {item.label}
              </a>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
